import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { SelectFilter, StatusDot, SectionLabel, CountLabel } from "../components/Bits";
import { REQUESTS, STAGE_META, STAGE_FILTERS, MILESTONE_STATE_META } from "../data/mockData";

/**
 * Service Requests.
 *
 * The design presents the detail as an in-place expansion rather than a separate
 * page, so that is what is built — expanding reveals the provenance line, the
 * milestone tracker and the media grid. `/admin/requests/:id` deep-links to a
 * request by opening it directly, which gives the brief's "detail view" a real
 * URL without inventing a second layout the design never specified.
 */
const ServiceRequests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useOutletContext();
  const [stage, setStage] = useState("all");
  const [expandedId, setExpandedId] = useState(id || "SR-4021");

  useEffect(() => {
    if (id) setExpandedId(id);
  }, [id]);

  const filtered = useMemo(
    () =>
      REQUESTS.filter((r) => {
        if (stage !== "all" && r.stage !== stage) return false;
        if (search && !r.title.toLowerCase().includes(search)) return false;
        return true;
      }),
    [stage, search]
  );

  const toggle = (requestId) => {
    const next = expandedId === requestId ? null : requestId;
    setExpandedId(next);
    // Keep the URL in step so a detail view can be linked to and shared.
    navigate(next ? `/admin/requests/${next}` : "/admin/requests", { replace: true });
  };

  return (
    <>
      <div className="admin-toolbar">
        <SelectFilter value={stage} onChange={setStage} options={STAGE_FILTERS} minWidth={190} />
        <div className="admin-spacer" />
        <CountLabel shown={filtered.length} total={REQUESTS.length} noun="projects" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.length === 0 && (
          <div className="card" style={{ textAlign: "center", opacity: 0.55, padding: 28 }}>
            Nothing matches those filters.
          </div>
        )}

        {filtered.map((r) => {
          const expanded = expandedId === r.id;
          const stageMeta = STAGE_META[r.stage];
          const locked = r.milestones.find((m) => m.state === "locked");

          return (
            <div className="card elev-sm" style={{ gap: 0 }} key={r.id}>
              <div className="admin-request-head" onClick={() => toggle(r.id)}>
                <span className="admin-request-chevron">{expanded ? "▾" : "▸"}</span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="admin-cell-strong">{r.title}</div>
                  <div className="admin-cell-sub">{r.id} · {r.homeowner} · {r.location}</div>
                </div>

                <span className="tag tag-neutral">{r.category}</span>

                <div className="admin-request-figures">
                  <div style={{ fontSize: 13 }}>{r.budget}</div>
                  <div style={{ fontSize: 11, color: "color-mix(in srgb, var(--color-text) 55%, transparent)" }}>
                    {r.timeline}
                  </div>
                </div>

                <StatusDot
                  color={stageMeta.color}
                  label={stageMeta.label}
                  className="admin-request-stage"
                />
              </div>

              {expanded && (
                <div className="admin-request-body">
                  <div className="admin-mono">resolved from: {r.provenance}</div>

                  <div>
                    <SectionLabel>Milestones</SectionLabel>

                    <div className="admin-milestone-bar">
                      {r.milestones.map((m) => (
                        <div
                          key={m.name}
                          title={`${m.name} — ${m.pct}%`}
                          style={{ flex: `${m.pct} 0 0%`, background: MILESTONE_STATE_META[m.state].color }}
                        />
                      ))}
                    </div>

                    <div className="admin-milestone-legend">
                      {r.milestones.map((m) => {
                        const meta = MILESTONE_STATE_META[m.state];
                        return (
                          <div className="admin-milestone-item" key={m.name}>
                            <span className="admin-milestone-mark" style={{ color: meta.color }}>
                              {meta.mark}
                            </span>
                            <span>{m.name}</span>
                            <span className="admin-milestone-pct">{m.pct}%</span>
                          </div>
                        );
                      })}
                    </div>

                    {locked && <div className="admin-lock-reason">{locked.lockReason}</div>}
                  </div>

                  <div>
                    <SectionLabel>Media</SectionLabel>
                    <div className="admin-media-grid">
                      {r.media.map((m, i) => (
                        <div className={`admin-media ${m.isAi ? "admin-media--ai" : ""}`} key={i}>
                          {m.isAi && <div className="admin-media-badge">AI CONCEPT</div>}
                          <div className="admin-media-label">
                            {m.isAi ? "AI concept — for illustration only" : m.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ServiceRequests;
